// =========================================================================
// Projections for a workflow step page. Route `/workflows/steps/:stepId`.
// Demo `renderWF`, source line 3696.
//
// Transformation layer — pure functions of explicit arguments. No React,
// no data fetching, no side effects.
// Fork-owned (zone C), P5a lane.
// =========================================================================

import { MODULE_BY_ID, type OemModule } from '@/versions/v1/shared/seed/modules';
import {
  WORKFLOW_STEP_BY_ID,
  workflowStepsForModule,
  type WorkflowStep,
  type WorkflowStepId,
} from '@/versions/v1/shared/seed/workflow-steps';

/**
 * Resolve a route segment to a step.
 *
 * The demo fell back to `WFS[0]` for an unknown id, so `/workflows/steps/typo`
 * rendered the installed-base register with the wrong URL in the bar. An
 * unknown id returns `null` here and the scene says so.
 */
export function stepFor(segment: string | undefined): WorkflowStep | null {
  if (!segment) return null;
  return WORKFLOW_STEP_BY_ID[decodeURIComponent(segment) as WorkflowStepId] ?? null;
}

/** Everything the step page needs that is not the body. */
export interface StepFrame {
  readonly step: WorkflowStep;
  readonly module: OemModule;
  /** `'04'`. */
  readonly moduleNumber: string;
  /** Every step of this module, in order. */
  readonly siblings: readonly WorkflowStep[];
  /** 1-based position — the demo's "Step 2 of 3 in this module". */
  readonly position: number;
  /** The previous step in this module, or `null` at the first. */
  readonly prev: WorkflowStep | null;
  /** The next step in this module, or `null` at the last. */
  readonly next: WorkflowStep | null;
  /**
   * The systems the step "Reads", as the demo printed them: the module's
   * systems with `' · '` rewritten to `' — '`, de-duplicated, first four.
   */
  readonly reads: readonly string[];
}

/**
 * Assemble the frame — demo line 3696.
 *
 * `prev` and `next` do **not** wrap. The module's step list is a sequence with
 * a beginning and an end, and the demo's own chrome said "Step 2 of 3", so
 * wrapping from the last step back to the first would contradict the label
 * sitting beside it. The module page is the way out at either end.
 */
export function stepFrame(step: WorkflowStep): StepFrame {
  const module = MODULE_BY_ID[step.mod];
  const siblings = workflowStepsForModule(step.mod);
  const index = siblings.findIndex((s) => s.id === step.id);

  return {
    step,
    module,
    moduleNumber: String(module.n).padStart(2, '0'),
    siblings,
    position: index + 1,
    prev: index > 0 ? siblings[index - 1] : null,
    next: index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : null,
    reads: [...new Set(module.systems.map((s) => s.replace(' · ', ' — ')))].slice(0, 4),
  };
}

/** A chip in "Also in the platform". */
export interface AlsoChip {
  readonly key: string;
  readonly label: string;
  readonly nav: WorkflowStep['also'][number]['nav'];
}

/**
 * The "Also in the platform" row — demo line 3710.
 *
 * The demo composed it from three sources in this order: the step's own
 * `also` chips, then every *other* step of the module, then a chip back to
 * the module. All three are kept; the sibling chips are what make a module's
 * steps feel like a set rather than a list of separate pages.
 */
export function alsoChips(frame: StepFrame): readonly AlsoChip[] {
  const own = frame.step.also.map(
    (a, i): AlsoChip => ({ key: `also:${a.label}:${i}`, label: a.label, nav: a.nav }),
  );
  const siblings = frame.siblings
    .filter((s) => s.id !== frame.step.id)
    .map(
      (s): AlsoChip => ({
        key: `step:${s.id}`,
        label: s.name,
        nav: { kind: 'show', action: { view: 'wf', sub: s.id } },
      }),
    );
  const back: AlsoChip = {
    key: `module:${frame.module.id}`,
    label: `Back to ${frame.moduleNumber} ${frame.module.name}`,
    nav: { kind: 'show', action: { view: 'module', sub: frame.module.id } },
  };

  return [...own, ...siblings, back];
}
